<template>
  <section class="section-contact">
    <div class="wrap">
      <div class="contact-information">
        <div class="image">
          <img :src="image" alt="">
        </div>
        <div class="text">
          {{ text }}
        </div>
      </div>

      <div class="contact-form">
        <div :class="{'show': !isSent}" class="form">
          <h3 id="formular">{{ title }}</h3>
          <form target="_self" @submit.prevent="submit">
            <input v-model="data.name" :class="{invalid: hasError('name')}" type="text" placeholder="Navn" />
            <input v-model="data.phone" :class="{invalid: hasError('phone')}" @keydown="validPhone" type="tel" placeholder="Telefonnummer" />
            <input v-model="data.email" :class="{invalid: hasError('email')}" type="email" placeholder="Email" />
            <input v-model="data.zip" :class="{invalid: hasError('zip')}" @keydown="validZip" type="text" placeholder="Postnummer" />
            <textarea v-model="data.message" name="name" rows="4" placeholder="Besked"></textarea>
            <button class="button filled" v-bind:disable="sending">Send</button>
            <p class="terms">
              Oplysninger om bankens indsamling og behandling af personoplysninger findes i Privatlivspolitik for Spar Nord Bank A/S – klik <a href="https://www.sparnord.dk/databeskyttelse" target="_blank">her</a> for at læse mere.
            </p>
          </form>
        </div>
        <div :class="{'show': isSent}" class="succes">
          <div class="message">
            <p>
              <strong>Tak for din henvendelse.</strong>
            </p>
            <p>{{ success }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import keycode from 'keycode'
import TweenLite from 'gsap'

import ValidatorRules from 'sparnord-mandrill-serverless/lib/validator/rules'
import ContactFields from '@/library/contact-fields'

export default {
  inject: ['$config'],
  async mounted () {
    await this.$nextTick()

    if (this.$route.hash) {
      setTimeout(() => {
        if (document.querySelector(this.$route.hash)) {
          TweenLite.to(window, 0, { scrollTo: { y: this.$route.hash, offsetY: 100 }, autoKill: false })
        }
      }, 1250)
    }
  },
  data: () => ({
    isSent: false,
    sending: false,
    hasBeenSubmitted: false,
    errors: {},
    data: {
      name: '',
      phone: '',
      email: '',
      zip: '',
      message: ''
    }
  }),
  computed: {
    title () {
      return this.$config ? this.$config.contactTitle : null
    },
    text () {
      return this.$config ? this.$config.contactText : null
    },
    image () {
      return this.$config ? this.$config.contactImage : null
    },
    success () {
      return this.$config ? this.$config.contactSucces : null
    }
  },
  methods: {
    async submit () {
      if (this.sending) {
        return
      }

      this.hasBeenSubmitted = true

      await this.$nextTick()

      if (this.hasErrors()) {
        return false
      }

      this.sending = true

      const fields = new ContactFields(this.data)

      fetch(fields.endpoint, {
        method: 'POST',
        body: new URLSearchParams(fields.query()),
        mode: 'no-cors'
      }).then((res) => {
        this.isSent = true
        this.sending = !this.isSent
        this.$analytics.pixel.track('Lead')
        this.$analytics.ga.event({ category: 'Lead', action: 'Click', label: 'lead formular' })
      })
    },

    validPhone (e) {
      let key = `${keycode(e)}`

      if (key === 'undefined') {
        return e.preventDefault()
      }

      if (e.metaKey || key.length > 1) {
        return
      }

      if (this.data.phone.length === 0 && e.keyCode === 187) {
        return
      }

      if (!key.match(/[0-9]/i)) {
        e.preventDefault()
      }
    },

    validZip (e) {
      let key = keycode(e)

      if (e.metaKey || key.length > 1) {
        return
      }

      if ((this.data.zip.length >= 4 && window.getSelection().toString().length < 1) || !key.match(/[0-9]/i)) {
        e.preventDefault()
      }
    },

    validate (key, value) {
      if (!this.hasBeenSubmitted || !ValidatorRules.hasOwnProperty(key)) {
        return true
      }

      let rule = ValidatorRules[key]

      if (rule.hasOwnProperty('match')) {
        if (!(new RegExp(rule.match)).test(value)) {
          return false
        }
      } else if (ValidatorRules[key].type !== typeof value) { // eslint-disable-line
        return false
      } else if (value.length < 1) {
        return false
      }

      return true
    },

    hasError (key) {
      let hasError = !this.validate(key, this.data[key])
      this.errors[key] = hasError

      return hasError
    },

    hasErrors () {
      return Object.values(this.errors).filter(val => !!val).length > 0
    }
  }
}
</script>

<style lang="scss" scoped>
/* Import from Spar Nord Assets */
@import '~@styles/global/index';
@import '~@styles/colors/variables';
@import '~@styles/mq/variables';
@import '~@styles/mixins/index';
@import '~@animations/easings/variables';
@import '~@animations/durations/variables';

.wrap {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;

  width: 1260px;
}

.section-contact {
  color: $color-black;
  background-color: $color-white;

  position: relative;
  z-index: 1;

  @media #{$below-breakpoint-768} {
    padding-top: 50px;
    padding-bottom: 50px;
  }
}

.contact-information, .contact-form {
  @media #{$below-breakpoint-1024} {
    flex: 0 0 100%;
  }
}

@at-root .internet-explorer {
  .contact-information, .contact-form {
    @media #{$above-breakpoint-1024} {
      max-width: 50%;
    }
  }
}

.contact-information {
  position: relative;
  flex: 1 1 auto;

  margin-right: 80px;

  @media #{$below-breakpoint-1024} {
    margin-bottom: 40px;
    flex: 0 0 100%;
  }

  .image {
    img {
      @include object-fit(cover, center);
    }

    @media #{$above-breakpoint-1024} {
      @include aspect-ratio(1, 1);
    }

    @media #{$below-breakpoint-1024} {
      @include aspect-ratio(1, 1);
    }
  }

  .text {
    background: $color-red;
    color: $color-white;

    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;

    @include font-size(22px, 25px);
    line-height: 1.2;
    font-weight: 700;
    padding: 40px 20px;

    @media #{$above-breakpoint-1024} {
      position: absolute;
      bottom: 0;
      right: -40px;

      transform: translateY(50%);

      z-index: 10;

      max-width: 360px;
      padding: 80px 40px;
    }
  }
}

.contact-form {
  flex: 0 1 570px;

  background-color: $color-grey-x;
  position: relative;

  padding-top: $global-padding * 4;
  padding-bottom: $global-padding * 4;

  padding-right: $global-padding * 4;
  padding-left: $global-padding * 4;

  @media #{$above-breakpoint-1024} {
    max-width: 570px;
  }

  @media #{$below-breakpoint-1024} {
    flex: 0 0 100%;

    padding-top: $global-padding * 2;
    padding-bottom: $global-padding * 2;

    padding-right: $global-padding;
    padding-left: $global-padding;
  }
}

// Typography
h3 {
  @include font-size(20px, 25px);
  font-weight: 700;
  color: $color-red;
  max-width: 80%;

  margin-bottom: calc(#{$global-padding} * 2);
}

button {
  margin-top: 30px;
  width: 100%;
}

// form
form {
  font-size: 16px;
  font-weight: 600;

  position: relative;

  div {
    display: flex;
    align-items: center;

    margin-top: 30px;
    margin-bottom: 25px;

    input {
      position: absolute;
      opacity: 0;
      width: auto;

      display: inline-block;

      & + label {
        position: relative;
        cursor: pointer;
        padding: 0;
      }

      & + label:before {
        content: '';
        margin-right: 10px;
        display: inline-block;
        vertical-align: text-top;

        width: 30px;
        height: 30px;
        background: black;

        transition: all $duration-normal $easeing-OutExpo;

        @at-root .internet-explorer #{&} {
          transform: translateY(0px);
        }
      }

      &:hover + label:before {
        background: $color-red;
      }

      &:focus + label:before {
        box-shadow: 0 0 0 3px rgba($color-black-x, .3);
      }

      &:checked + label:before {
        background: $color-red;
      }

      &:disabled + label {
        color: $color-gold;
        cursor: auto;
      }

      &:disabled + label:before {
        box-shadow: none;
        background: $color-grey-xx;
      }

      &:checked + label:after {
        content: '\2713';
        position: absolute;
        left: 8px;
        top: 9px;
      }
    }
  }

  label {
    font-size: 16px;
    font-weight: 600;

    user-select: none;

    cursor: pointer;

    span {
      user-select: none;
      transform: translateY(8px);
      display: inline-block;
    }
  }

  input, textarea {
    margin-bottom: 25px;
    display: block;

    font-size: 16px;
    font-weight: 600;

    width: 100%;
  }

  input[type="text"] {
    @media #{$below-breakpoint-768} {
      &::-webkit-input-placeholder {
        line-height: 1;
        transform: translateY(4px)
      }
    }
  }

  input[type="text"],
  input[type="email"],
  input[type="tel"] {
    padding: 0;
    height: 35px;
    background-color: transparent;

    border-bottom: 2px solid $color-grey-xx;
    color: $color-grey-xx;

    transition: all $duration-normal $easeing-OutExpo;

     &::-webkit-input-placeholder {
       color: $color-grey-xx;
     }

     &:focus {
       border-color: $color-red;
     }

      &:not(:placeholder-shown) {
        border-color: $color-red;
        color: $color-black;
      }

     &.invalid {
       border-color: $color-red;
       color: $color-red;

       &::-webkit-input-placeholder {
         color: $color-red;
       }
     }

     @media #{$below-breakpoint-768} {
        &::-webkit-input-placeholder {
          line-height: 1;
          transform: translateY(4px);
        }
      }

     -webkit-appearance: none;
      border-radius: 0;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus
  input:-webkit-autofill,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover
  textarea:-webkit-autofill:focus,
  select:-webkit-autofill,
  select:-webkit-autofill:hover,
  select:-webkit-autofill:focus {
    border-color: $color-red-x;
    background-color: transparent !important;
    -webkit-text-fill-color: $color-black;
    -webkit-box-shadow: 0 0 0px 1000px rgba($color-white, 1) inset;
    transition: all $duration-normal $easeing-OutExpo;
  }

  textarea {
    resize: none;
    padding: 20px;
    overflow: auto;
    -webkit-appearance: none;
     border-radius: 0;
    background-color: white;

    margin-top: 35px;

    border: 2px solid $color-grey-xx;

    &::-webkit-input-placeholder {
      color: $color-grey-xx;
    }

    &:focus {
      outline-color: $color-red;
      outline-style: solid;
      outline-width: 2px;
    }

    &:not(:placeholder-shown) {
      border-color: $color-red;
    }

    &.invalid {
      border: 2px solid red;
    }
  }

  .select {
    position: relative;

    display: flex;
    align-items: center;

    height: 30px;
    line-height: 1;
    background: $color-white;
    overflow: hidden;

    margin: 0;

    width: 100%;
  }

  select {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0 0 0 8px;
    color: $color-black;
    font-size: 14px;
    line-height: 1.3;
    cursor: pointer;
  }

  select::-ms-expand {
    display: none;
  }

  /* Arrow */
  .select::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 8px;
    bottom: 0;

    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;

    border-top: 6px solid $color-black;
    pointer-events: none;

    transform: translateY(-50%);
  }

  /* Transition */
  .select:hover::after {
    border-top-color: $color-red;
  }

  .select::after {
    -webkit-transition: $duration-normal all $easeing-OutExpo;
    -o-transition: $duration-normal all $easeing-OutExpo;
    transition: $duration-normal all $easeing-OutExpo;
  }
}

// Table
table {
  width: 100%;

  margin: 0 -5px;

  font-size: 16px;
  font-weight: 600;
}

table tr td {
  padding: 5px;
}

table tr {
  td:first-child {
    width: 100%;
  }

  td:nth-child(2), td:nth-child(4) {
    width: 45px;
  }

  td:nth-child(3) {
    width: 4px;
  }
}

.form {
  opacity: 0;

  &.show {
    opacity: 1;
  }
}

.succes {
  opacity: 0;

  position: absolute;
  top: 0;
  left: 0;

  height: 100%;
  width: 100%;

  text-align: center;

  margin-left: auto;
  margin-right: auto;

  display: flex;
  align-items: center;
  justify-content: center;

  visibility: hidden;

  .message {
    padding: 0 40px;
    line-height: 1.65;
    @include font-size(14px, 18px);

    strong {
      font-weight: 700;
      @include font-size(18px, 22px);
    }
  }

  &.show {
    opacity: 1;
    visibility: visible;
  }
}

.terms {
  font-size: 75%;
  line-height: 1.48;

  opacity: 0.75;

  display: block;
  margin-top: 20px;

  a {
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/sections/contact.vue